import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { auth } from "@/lib/api";
import { setLanguage } from "@/i18n";
import { applyTextSize } from "@/lib/textSize";
import { setSkipExternalLinkConfirm } from "@/lib/externalLinkPrefs";
import { clearIntendedRoute } from "@/lib/authReturnTo";

const AuthCtx = createContext(null);

const applyUserPrefs = (user) => {
  if (!user) return;
  if (user.language) setLanguage(user.language);
  if (user.text_size) applyTextSize(user.text_size);
};

export const AuthProvider = ({ children }) => {
  const [user, _setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // When signOut() runs, ProtectedRoute re-renders with user=null and would
  // otherwise capture the current URL (e.g. /settings) into `returnTo`
  // BEFORE Settings' explicit `navigate("/login")` completes — a race that
  // leaves a stale pointer to the just-signed-out user's settings page.
  // ProtectedRoute checks this ref inside its capture effect and skips.
  // Held on the context (not React state) so a flip doesn't trigger an
  // extra render.
  const suppressCaptureRef = useRef(false);

  const setUser = useCallback((u) => {
    _setUser(u);
    applyUserPrefs(u);
  }, []);

  const refresh = useCallback(async () => {
    // Retry ONCE on transient failures (network flake, iOS BFCache restore
    // that fires /auth/me before the cookie jar is fully re-hydrated, brief
    // 5xx from the backend during a cold start). 401 is a genuine "signed
    // out" verdict — don't retry that. Prior behavior was one attempt →
    // user=null, which sent legitimately-signed-in users to /login on any
    // transient hiccup, most visibly after a swipe-back gesture that
    // triggered a BFCache reload.
    const attempt = async () => {
      const { data } = await auth.me();
      return data;
    };
    try {
      setUser(await attempt());
    } catch (e) {
      const status = e?.response?.status;
      // Definitive "not signed in" — no point retrying.
      if (status === 401 || status === 403) {
        if (process.env.NODE_ENV !== "production") console.info("[auth] /auth/me — not signed in or session expired");
        _setUser(null);
      } else {
        // Transient (network / 5xx / offline). Wait a beat and try once more.
        try {
          await new Promise((r) => setTimeout(r, 500));
          setUser(await attempt());
        } catch (e2) {
          if (process.env.NODE_ENV !== "production") console.info("[auth] /auth/me — transient failure after retry, treating as signed-out:", e2?.message);
          _setUser(null);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [setUser]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    // Suppress ProtectedRoute's returnTo capture for the brief window between
    // `user=null` propagating and the Settings page's explicit navigate('/login').
    // Without this, ProtectedRoute re-renders on /settings with user=null and
    // captures /settings into returnTo — leaving a stale pointer that would
    // resume the sign-out screen on the NEXT sign-in.
    suppressCaptureRef.current = true;
    try {
      await auth.logout();
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("[auth] logout request failed:", e);
    }
    // Legacy cleanup — older builds persisted the token in localStorage.
    // Clear that key so users upgrading from a stale cache aren't left with
    // a stray value in DevTools. Current builds never write here.
    try {
      localStorage.removeItem("session_token");
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("[auth] could not clear legacy token:", e);
    }
    // Reset the session-only "don't ask again for external links" flag so
    // the next signed-in user starts with the safety confirmation restored.
    setSkipExternalLinkConfirm(false);
    // Clear any lingering "return to this URL after re-auth" pointer — an
    // explicit sign-out means the user wants to start fresh at /home, not
    // resume whatever page bounced them here previously.
    clearIntendedRoute();
    _setUser(null);
    // Release the capture suppression after the DOM has a chance to settle
    // on /login (a single macrotask is plenty — react-router's navigate is
    // synchronous, so by the time this timer fires we're already there).
    setTimeout(() => { suppressCaptureRef.current = false; }, 750);
  }, []);

  // Memoize the context value so consumers only re-render when the pieces
  // they actually read change (previously every AuthProvider render created
  // a fresh object identity, forcing every useAuth() consumer to re-render).
  //
  // `suppressCaptureRef` is a MutableRefObject — its identity is stable for
  // the lifetime of the provider, so adding it to the value doesn't cause
  // extra re-renders. ProtectedRoute reads it to short-circuit its capture
  // during a sign-out flow.
  const value = useMemo(
    () => ({ user, setUser, loading, refresh, signOut, suppressCaptureRef }),
    [user, setUser, loading, refresh, signOut]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => useContext(AuthCtx);
